import { Outlet, useNavigate, useRouterState } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { useTranslation } from '@eon/i18n';
import {
  Avatar,
  AvatarFallback,
  BrandMark,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Sidebar,
  useTheme,
} from '@eon/ui';
import { hooks } from '@eon/api-client';
import { isOrgOperator, isSystemAdmin } from '@eon/types';
import {
  ChevronsUpDown,
  KeyRound,
  LogOut,
  Menu,
  Moon,
  Search,
  Sun,
  User,
} from 'lucide-react';
import { LanguageSwitcher } from '../components/LanguageSwitcher';
import { ChangePasswordDialog } from '../components/portal/ChangePasswordDialog';
import { EditAccountDialog } from '../components/portal/EditAccountDialog';
import { useNavConfig, usePageTitleMap } from '../auth/useNavConfig';

function usePageTitle(role: ReturnType<typeof hooks.useMe>['data']) {
  const { location } = useRouterState();
  const titleMap = usePageTitleMap(role?.role);
  const segment = location.pathname === '/' ? '/' : `/${location.pathname.split('/')[1]}`;
  return titleMap[segment] ?? '';
}

function AdminDeviceUpdates() {
  hooks.useDeviceUpdates();
  return null;
}

export function AppLayout() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { location } = useRouterState();
  const me = hooks.useMe();
  const logout = hooks.useLogout();
  const { theme, setTheme } = useTheme();
  const nav = useNavConfig(me.data?.role);
  const pageTitle = usePageTitle(me.data);
  const showAdminChrome = nav.showAdminHeader && isSystemAdmin(me.data?.role);
  const showPortalHeader = isOrgOperator(me.data?.role) && !showAdminChrome;
  const [editAccountOpen, setEditAccountOpen] = useState(false);
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // The mobile drawer should never survive a route change (e.g. browser back).
  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  const active = location.pathname === '/' ? '/' : `/${location.pathname.split('/')[1]}`;
  const initials = (me.data?.name ?? me.data?.email ?? '?').slice(0, 2).toUpperCase();

  const userFooter = (
    <div className="flex items-center gap-2">
      <Avatar className={showAdminChrome ? 'h-8 w-8 shrink-0' : undefined}>
        <AvatarFallback
          className={
            showAdminChrome
              ? 'bg-primary/15 text-primary text-xs font-semibold'
              : undefined
          }
        >
          {initials}
        </AvatarFallback>
      </Avatar>
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium">{me.data?.name ?? '—'}</p>
        <p className="text-muted-foreground truncate text-xs">{me.data?.email ?? ''}</p>
      </div>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon" className={showAdminChrome ? 'h-7 w-7 shrink-0' : undefined}>
            <ChevronsUpDown className={showAdminChrome ? 'h-3.5 w-3.5' : 'h-4 w-4'} />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" side="top" className="w-52">
          <DropdownMenuLabel className={showAdminChrome ? 'text-xs text-muted-foreground font-normal' : undefined}>
            {me.data?.name ?? me.data?.email}
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem onSelect={() => setTheme(theme === 'dark' ? 'light' : 'dark')}>
            {theme === 'dark' ? (
              <Sun className="mr-2 h-4 w-4" />
            ) : (
              <Moon className="mr-2 h-4 w-4" />
            )}
            {theme === 'dark' ? t('theme.light') : t('theme.dark')}
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            onSelect={() =>
              logout.mutate(undefined, { onSettled: () => navigate({ to: '/login' }) })
            }
            className={showAdminChrome ? 'text-destructive focus:text-destructive' : undefined}
          >
            <LogOut className="mr-2 h-4 w-4" /> {t('auth.logout')}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );

  if (showAdminChrome) {
    return (
      <div className="bg-background text-foreground flex h-dvh overflow-hidden">
        {isSystemAdmin(me.data?.role) ? <AdminDeviceUpdates /> : null}
        <Sidebar
          brand={t(nav.brandKey)}
          brandLogo={<BrandMark size="sm" />}
          sections={nav.sections}
          active={active}
          onNavigate={(to) => void navigate({ to })}
          footer={userFooter}
          mobileOpen={sidebarOpen}
          onMobileClose={() => setSidebarOpen(false)}
        />
        <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
          <header className="glass sticky top-0 z-30 flex h-14 shrink-0 items-center gap-2 border-b px-4 sm:gap-4 sm:px-6">
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 shrink-0 lg:hidden"
              onClick={() => setSidebarOpen(true)}
              aria-label={t('common.menu')}
            >
              <Menu className="h-5 w-5" />
            </Button>
            <h2 className="font-display min-w-0 flex-1 truncate text-base font-semibold tracking-tight text-foreground">
              {pageTitle}
            </h2>
            <div className="relative hidden sm:block">
              <Search className="text-muted-foreground absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2" />
              <input
                type="search"
                placeholder={t('common.search')}
                className="bg-muted/50 text-foreground placeholder:text-muted-foreground h-8 rounded-lg border border-border/50 pl-8 pr-3 text-sm outline-none focus:border-primary/50 focus:bg-muted/80 transition-all w-48 focus:w-64"
              />
            </div>
            <LanguageSwitcher />
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
              title={theme === 'dark' ? t('theme.light') : t('theme.dark')}
            >
              {theme === 'dark' ? (
                <Sun className="h-4 w-4 text-muted-foreground" />
              ) : (
                <Moon className="h-4 w-4 text-muted-foreground" />
              )}
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="h-8 w-8 rounded-full p-0">
                  <Avatar className="h-8 w-8">
                    <AvatarFallback className="bg-primary/15 text-primary text-xs font-semibold">
                      {initials}
                    </AvatarFallback>
                  </Avatar>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-52">
                <DropdownMenuLabel className="font-normal">
                  <div className="flex flex-col space-y-0.5">
                    <p className="text-sm font-medium">{me.data?.name ?? '—'}</p>
                    <p className="text-xs text-muted-foreground">{me.data?.email ?? ''}</p>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onSelect={() =>
                    logout.mutate(undefined, { onSettled: () => navigate({ to: '/login' }) })
                  }
                  className="text-destructive focus:text-destructive"
                >
                  <LogOut className="mr-2 h-4 w-4" /> {t('auth.logout')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </header>
          <main className="scrollbar-thin flex-1 overflow-y-auto bg-dot-grid">
            <div className="mx-auto max-w-screen-2xl px-4 py-4 sm:px-6 sm:py-6">
              <Outlet />
            </div>
          </main>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-background text-foreground flex h-dvh overflow-hidden">
      <Sidebar
        brand={t(nav.brandKey)}
        brandLogo={<BrandMark size="sm" />}
        items={nav.items}
        active={active}
        onNavigate={(to) => void navigate({ to })}
        footer={userFooter}
        mobileOpen={sidebarOpen}
        onMobileClose={() => setSidebarOpen(false)}
      />
      <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
        {showPortalHeader ? (
          <header className="glass sticky top-0 z-30 flex h-14 shrink-0 items-center gap-2 border-b px-4 sm:gap-4 sm:px-6">
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 shrink-0 lg:hidden"
              onClick={() => setSidebarOpen(true)}
              aria-label={t('common.menu')}
            >
              <Menu className="h-5 w-5" />
            </Button>
            <h2 className="font-display min-w-0 flex-1 truncate text-base font-semibold tracking-tight text-foreground">
              {pageTitle}
            </h2>
            <LanguageSwitcher />
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
              title={theme === 'dark' ? t('theme.light') : t('theme.dark')}
            >
              {theme === 'dark' ? (
                <Sun className="h-4 w-4 text-muted-foreground" />
              ) : (
                <Moon className="h-4 w-4 text-muted-foreground" />
              )}
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="h-8 w-8 rounded-full p-0">
                  <Avatar className="h-8 w-8">
                    <AvatarFallback className="bg-primary/15 text-primary text-xs font-semibold">
                      {initials}
                    </AvatarFallback>
                  </Avatar>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-52">
                <DropdownMenuLabel className="font-normal">
                  <div className="flex flex-col space-y-0.5">
                    <p className="text-sm font-medium">{me.data?.name ?? '—'}</p>
                    <p className="text-xs text-muted-foreground">{me.data?.email ?? ''}</p>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onSelect={() => setEditAccountOpen(true)}>
                  <User className="mr-2 h-4 w-4" />
                  {t('portal.account.editAccount')}
                </DropdownMenuItem>
                <DropdownMenuItem onSelect={() => setChangePasswordOpen(true)}>
                  <KeyRound className="mr-2 h-4 w-4" />
                  {t('portal.account.changePassword')}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onSelect={() =>
                    logout.mutate(undefined, { onSettled: () => navigate({ to: '/login' }) })
                  }
                  className="text-destructive focus:text-destructive"
                >
                  <LogOut className="mr-2 h-4 w-4" /> {t('auth.logout')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </header>
        ) : (
          <header className="glass sticky top-0 z-30 flex h-14 shrink-0 items-center gap-2 border-b px-4 lg:hidden">
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 shrink-0"
              onClick={() => setSidebarOpen(true)}
              aria-label={t('common.menu')}
            >
              <Menu className="h-5 w-5" />
            </Button>
            <span className="font-display min-w-0 truncate text-base font-semibold tracking-tight text-foreground">
              {pageTitle || t(nav.brandKey)}
            </span>
          </header>
        )}
        <main className="scrollbar-thin flex-1 overflow-y-auto">
          <div className="container mx-auto px-4 py-6 sm:px-6 sm:py-8">
            <Outlet />
          </div>
        </main>
      </div>
      {showPortalHeader ? (
        <>
          <EditAccountDialog open={editAccountOpen} onOpenChange={setEditAccountOpen} />
          <ChangePasswordDialog open={changePasswordOpen} onOpenChange={setChangePasswordOpen} />
        </>
      ) : null}
    </div>
  );
}
